import React, {useMemo, useRef, useState} from "react";
import {Autocomplete, TextField} from "@mui/material";
import Papa from "papaparse";
import styles from "./CsvImportPanel.module.css";
import {useTranslation} from "react-i18next";

export type CsvImportResult = {
    tasks: {
        title: string;
        description?: string;
    }[];
};

type CsvState =
    | "upload"
    | "parsing"
    | "mapping"
    | "applying"
    | "done";

type Props = {
    onApply: (result: CsvImportResult) => void;
};

type HeaderOption = {
    label: string;
    value: string;
};

const CsvImportPanel: React.FC<Props> = ({onApply}) => {
    const {t} = useTranslation();
    const [state, setState] = useState<CsvState>("upload");
    const [file, setFile] = useState<File | null>(null);
    const [headers, setHeaders] = useState<string[]>([]);
    const [rows, setRows] = useState<Record<string, string>[]>([]);
    const [mapping, setMapping] = useState<{ title?: string; description?: string }>({});
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);

    const headerOptions = useMemo<HeaderOption[]>(
        () => headers.map((header) => ({label: header, value: header})),
        [headers]
    );
    const selectedTitleOption = headerOptions.find((option) => option.value === mapping.title) ?? null;
    const selectedDescriptionOption = headerOptions.find((option) => option.value === mapping.description) ?? null;

    const reset = () => {
        setState("upload");
        setFile(null);
        setHeaders([]);
        setRows([]);
        setMapping({});
    };

    const handleFile = (f: File) => {
        setFile(f);
        setState("parsing");

        Papa.parse(f, {
            header: true,
            skipEmptyLines: true,
            complete: (res) => {
                setHeaders(res.meta.fields ?? []);
                setRows(res.data as Record<string, string>[]);
                setState("mapping");
            },
        });
    };

    const apply = () => {
        setState("applying");

        const tasks = rows
            .map(r => ({
                title: r[mapping.title!]?.trim(),
                description: mapping.description
                    ? r[mapping.description]?.trim()
                    : "",
            }))
            .filter(t => t.title);

        onApply({tasks});
        setState("done");
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return t("fileSize.zero");

        const k = 1024;
        const sizes = ["bytes", "kb", "mb", "gb"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        const value = bytes / Math.pow(k, i);

        return t(`fileSize.${sizes[i]}`, {
            value: value.toFixed(1),
        });
    };

    return (
        <div className={`${styles.panel} ${state === "upload" ? styles.dotted : ""}`}>
            {(state === "mapping" || state === "applying" || state === "done") && file && (
                <div className={styles.fileRow}>
                    <div className={styles.fileInfo}>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M6.75016 5.25016V1.59766C6.38419 1.69889 6.05029 1.8923 5.78041 2.15941L3.65941 4.28041C3.3923 4.55029 3.19889 4.88419 3.09766 5.25016H6.75016Z"
                                fill="#3F3F3F"/>
                            <path
                                d="M15.75 6.75H14.946C14.9808 6.66392 14.9991 6.57208 15 6.47925V3C15.0062 2.60897 14.857 2.23143 14.5853 1.95023C14.3135 1.66902 13.9413 1.5071 13.5502 1.5H8.25V5.25C8.25 5.64782 8.09197 6.02936 7.81065 6.31066C7.52933 6.59197 7.14782 6.75 6.75 6.75H2.25C2.05108 6.75 1.86032 6.82902 1.71967 6.96967C1.57902 7.11032 1.5 7.30108 1.5 7.5V14.25C1.5 14.4489 1.57902 14.6397 1.71967 14.7803C1.86032 14.9209 2.05108 15 2.25 15H3C2.99378 15.3911 3.14292 15.7686 3.4147 16.0498C3.6865 16.3309 4.05873 16.4929 4.44975 16.5H13.5502C13.9413 16.4929 14.3135 16.3309 14.5853 16.0498C14.857 15.7686 15.0062 15.3911 15 15H15.75C15.9489 15 16.1397 14.9209 16.2803 14.7803C16.4209 14.6397 16.5 14.4489 16.5 14.25V7.5C16.5 7.30108 16.4209 7.11032 16.2803 6.96967C16.1397 6.82902 15.9489 6.75 15.75 6.75ZM9 8.121L9.72075 8.136C9.81923 8.1381 9.91635 8.15955 10.0066 8.19915C10.0968 8.23875 10.1782 8.29567 10.2465 8.36677C10.3147 8.43795 10.3681 8.52173 10.4039 8.61353C10.4397 8.70533 10.4571 8.80328 10.455 8.90175C10.4529 9.00023 10.4314 9.09735 10.3918 9.18758C10.3523 9.2778 10.2953 9.35925 10.2242 9.4275C10.1531 9.49567 10.0693 9.54915 9.97748 9.58492C9.88567 9.6207 9.78772 9.6381 9.68925 9.636L8.97975 9.621C8.91697 9.62025 8.85623 9.6429 8.8092 9.68445C8.76225 9.72607 8.7324 9.7836 8.7255 9.846C8.71987 9.90825 8.73892 9.97027 8.77868 10.0186C8.81842 10.0669 8.87558 10.0976 8.93775 10.104L9.3405 10.1483C9.62288 10.172 9.8964 10.2584 10.1412 10.4011C10.386 10.5439 10.5959 10.7392 10.7557 10.9732C10.8875 11.1897 10.9679 11.4335 10.9905 11.6859C11.0131 11.9383 10.9775 12.1925 10.8863 12.429C10.7654 12.7753 10.5301 13.0699 10.2192 13.2646C9.9084 13.4593 9.54052 13.5423 9.17625 13.5H8.331C8.1321 13.5 7.9413 13.4209 7.80067 13.2803C7.66005 13.1397 7.581 12.9489 7.581 12.75C7.581 12.5511 7.66005 12.3603 7.80067 12.2197C7.9413 12.0791 8.1321 12 8.331 12H9.17625C9.27487 12.009 9.37432 11.9969 9.468 11.9648C9.49305 11.9053 9.5001 11.8399 9.48825 11.7765C9.4056 11.6942 9.29505 11.646 9.1785 11.6415L8.77575 11.5972C8.32358 11.5504 7.90807 11.3274 7.61903 10.9765C7.33006 10.6255 7.19083 10.175 7.2315 9.72225C7.27586 9.2838 7.48124 8.87737 7.80787 8.58165C8.13457 8.28585 8.5593 8.12175 9 8.121ZM3 11.5147V10.2353C3.0008 9.70897 3.21021 9.20445 3.58235 8.83237C3.95448 8.46022 4.45897 8.25082 4.98525 8.25H5.74875C5.94766 8.25 6.13843 8.32905 6.27908 8.46968C6.41973 8.6103 6.49875 8.8011 6.49875 9C6.49875 9.1989 6.41973 9.3897 6.27908 9.53032C6.13843 9.67095 5.94766 9.75 5.74875 9.75H4.98525C4.85655 9.75 4.73313 9.80115 4.64212 9.89213C4.55113 9.9831 4.5 10.1066 4.5 10.2353V11.5147C4.5 11.6434 4.55113 11.7669 4.64212 11.8579C4.73313 11.9488 4.85655 12 4.98525 12H5.74875C5.94766 12 6.13843 12.0791 6.27908 12.2197C6.41973 12.3603 6.49875 12.5511 6.49875 12.75C6.49875 12.9489 6.41973 13.1397 6.27908 13.2803C6.13843 13.4209 5.94766 13.5 5.74875 13.5H4.98525C4.45891 13.4994 3.9543 13.2901 3.58213 12.9179C3.20995 12.5457 3.00059 12.0411 3 11.5147ZM14.9633 9.2325L13.7955 12.8123C13.7463 12.9626 13.6508 13.0936 13.5227 13.1863C13.3946 13.2792 13.2404 13.3291 13.0823 13.329H13.074C12.9145 13.3272 12.7597 13.2746 12.6321 13.1789C12.5045 13.0831 12.4108 12.9492 12.3645 12.7965L11.2823 9.2175C11.2262 9.0276 11.2474 8.8233 11.3415 8.649C11.4355 8.47477 11.5946 8.3448 11.7841 8.28735C11.9735 8.22997 12.1781 8.24977 12.353 8.34255C12.5279 8.43525 12.659 8.59342 12.7178 8.7825L13.1093 10.0778L13.5367 8.7675C13.5644 8.67052 13.6114 8.58015 13.6748 8.5017C13.7382 8.42325 13.8168 8.35845 13.9058 8.31112C13.9949 8.26373 14.0926 8.23485 14.1931 8.22615C14.2935 8.21737 14.3948 8.229 14.4906 8.26035C14.5865 8.29162 14.6751 8.34188 14.7511 8.40825C14.8271 8.47455 14.8889 8.55548 14.9329 8.64622C14.9768 8.73698 15.0021 8.83568 15.007 8.9364C15.012 9.03713 14.9966 9.13785 14.9618 9.2325H14.9633Z"
                                fill="#3F3F3F"/>
                        </svg>
                        <span className={styles.fileName}>{file.name}</span>
                        <span className={styles.fileSize}>
                            {formatFileSize(file.size)}
                        </span>
                    </div>
                    <button onClick={reset} className={`${styles.uploadBtn} ${styles.deleteBtn}`}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M1.75 3.5013H2.91667M2.91667 3.5013H12.25M2.91667 3.5013V11.668C2.91667 11.9774 3.03958 12.2741 3.25838 12.4929C3.47717 12.7117 3.77391 12.8346 4.08333 12.8346H9.91667C10.2261 12.8346 10.5228 12.7117 10.7416 12.4929C10.9604 12.2741 11.0833 11.9774 11.0833 11.668V3.5013M4.66667 3.5013V2.33464C4.66667 2.02522 4.78958 1.72847 5.00838 1.50968C5.22717 1.29089 5.52391 1.16797 5.83333 1.16797H8.16667C8.47609 1.16797 8.77283 1.29089 8.99162 1.50968C9.21042 1.72847 9.33333 2.02522 9.33333 2.33464V3.5013"
                                stroke="#3F3F3F" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
            )}

            {state === "upload" && (
                <div
                    className={`${styles.dropZone} ${isDragOver ? styles.dragOver : ""}`}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                    }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                        if (e.dataTransfer.files?.[0]) {
                            handleFile(e.dataTransfer.files[0]);
                        }
                    }}
                >
                    <p className={styles.dropText}>
                        {t("csv.dropHere")}
                    </p>
                    <p className={styles.or}>{t("or")}</p>

                    <button
                        type="button"
                        className={styles.uploadBtn}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M8.25 12V5.8875L6.3 7.8375L5.25 6.75L9 3L12.75 6.75L11.7 7.8375L9.75 5.8875V12H8.25ZM4.5 15C4.0875 15 3.73438 14.8531 3.44063 14.5594C3.14688 14.2656 3 13.9125 3 13.5V11.25H4.5V13.5H13.5V11.25H15V13.5C15 13.9125 14.8531 14.2656 14.5594 14.5594C14.2656 14.8531 13.9125 15 13.5 15H4.5Z"
                                fill="#3F3F3F"/>
                        </svg>
                        {t("csv.uploadFromPc")}
                    </button>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        hidden
                        onChange={(e) => e.target.files && handleFile(e.target.files[0])}
                    />
                </div>
            )}

            {state === "parsing" && <div className={styles.spinner}>{t("csv.parsing")}</div>}

            {state === "mapping" && (
                <>
                    <div className={styles.mappingFieldWrp}>
                        <label className={styles.mappingField}>
                            <div className={styles.labelRow}>
                                <span className={styles.labelName}>{t("csvMapping.titleLabel")}</span>

                                <div className={styles.infoWrapper}>
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M7 14C8.85652 14 10.637 13.2625 11.9497 11.9497C13.2625 10.637 14 8.85652 14 7C14 5.14348 13.2625 3.36301 11.9497 2.05025C10.637 0.737498 8.85652 0 7 0C5.14348 0 3.36301 0.737498 2.05025 2.05025C0.737498 3.36301 0 5.14348 0 7C0 8.85652 0.737498 10.637 2.05025 11.9497C3.36301 13.2625 5.14348 14 7 14ZM7.81375 5.7645L6.93875 9.88138C6.8775 10.1789 6.96413 10.3477 7.20475 10.3477C7.3745 10.3477 7.63088 10.2865 7.805 10.1325L7.728 10.4965C7.47687 10.7993 6.923 11.0197 6.44613 11.0197C5.831 11.0197 5.56938 10.6505 5.73913 9.86562L6.38487 6.83113C6.44088 6.57475 6.39013 6.482 6.13375 6.41988L5.73913 6.349L5.81087 6.01562L7.81375 5.7645ZM7 4.8125C6.76794 4.8125 6.54538 4.72031 6.38128 4.55622C6.21719 4.39212 6.125 4.16956 6.125 3.9375C6.125 3.70544 6.21719 3.48288 6.38128 3.31878C6.54538 3.15469 6.76794 3.0625 7 3.0625C7.23206 3.0625 7.45462 3.15469 7.61872 3.31878C7.78281 3.48288 7.875 3.70544 7.875 3.9375C7.875 4.16956 7.78281 4.39212 7.61872 4.55622C7.45462 4.72031 7.23206 4.8125 7 4.8125Z"
                                            fill="#9865DA"/>
                                    </svg>
                                    <span className={styles.tooltip}>
                                        {t("csvMapping.titleHint")}
                                    </span>
                                </div>
                            </div>

                            <Autocomplete
                                options={headerOptions}
                                value={selectedTitleOption}
                                onChange={(_, option) => setMapping((prev) => ({...prev, title: option?.value}))}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                noOptionsText={t("csvMapping.noOptions")}
                                disablePortal
                                fullWidth
                                renderInput={(params) => (
                                    <TextField {...params} placeholder={t("csvMapping.titlePlaceholder")}/>
                                )}
                                slotProps={{
                                    paper: {className: styles.autocompletePaper},
                                    listbox: {className: styles.autocompleteListbox},
                                }}
                            />
                        </label>

                        <label className={styles.mappingField}>
                            <div className={styles.labelRow}>
                                <span className={styles.labelName}>{t("csvMapping.descriptionLabel")}</span>

                                <div className={styles.infoWrapper}>
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M7 14C8.85652 14 10.637 13.2625 11.9497 11.9497C13.2625 10.637 14 8.85652 14 7C14 5.14348 13.2625 3.36301 11.9497 2.05025C10.637 0.737498 8.85652 0 7 0C5.14348 0 3.36301 0.737498 2.05025 2.05025C0.737498 3.36301 0 5.14348 0 7C0 8.85652 0.737498 10.637 2.05025 11.9497C3.36301 13.2625 5.14348 14 7 14ZM7.81375 5.7645L6.93875 9.88138C6.8775 10.1789 6.96413 10.3477 7.20475 10.3477C7.3745 10.3477 7.63088 10.2865 7.805 10.1325L7.728 10.4965C7.47687 10.7993 6.923 11.0197 6.44613 11.0197C5.831 11.0197 5.56938 10.6505 5.73913 9.86562L6.38487 6.83113C6.44088 6.57475 6.39013 6.482 6.13375 6.41988L5.73913 6.349L5.81087 6.01562L7.81375 5.7645ZM7 4.8125C6.76794 4.8125 6.54538 4.72031 6.38128 4.55622C6.21719 4.39212 6.125 4.16956 6.125 3.9375C6.125 3.70544 6.21719 3.48288 6.38128 3.31878C6.54538 3.15469 6.76794 3.0625 7 3.0625C7.23206 3.0625 7.45462 3.15469 7.61872 3.31878C7.78281 3.48288 7.875 3.70544 7.875 3.9375C7.875 4.16956 7.78281 4.39212 7.61872 4.55622C7.45462 4.72031 7.23206 4.8125 7 4.8125Z"
                                            fill="#9865DA"/>
                                    </svg>
                                    <span className={styles.tooltip}>
                                        {t("csvMapping.descriptionHint")}
                                    </span>
                                </div>
                            </div>

                            <Autocomplete
                                options={headerOptions}
                                value={selectedDescriptionOption}
                                onChange={(_, option) => setMapping((prev) => ({...prev, description: option?.value}))}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                noOptionsText={t("csvMapping.noOptions")}
                                disablePortal
                                fullWidth
                                renderInput={(params) => (
                                    <TextField {...params} placeholder={t("csvMapping.descriptionPlaceholder")}/>
                                )}
                                slotProps={{
                                    paper: {className: styles.autocompletePaper},
                                    listbox: {className: styles.autocompleteListbox},
                                }}
                            />
                        </label>
                    </div>

                    <button
                        disabled={!mapping.title}
                        onClick={apply}
                        className={`${styles.uploadBtn} ${styles.confirmBtn}`}
                    >
                        {t("apply")}
                    </button>
                </>
            )}

            {state === "applying" && <div className={styles.spinner}>{t("csv.applying")}</div>}

            {state === "done" && (
                <div className={styles.done}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.3346 4L6.0013 11.3333L2.66797 8" stroke="#9865DA" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    {t("csv.done")}
                </div>
            )}
        </div>
    );
};

export default CsvImportPanel;
